import validateDateFromStorage from 'flux/utils/validateDateFromStorage';
import Fluxxor from 'fluxxor';
import omit from 'lodash.omit';
import uniqWith from 'lodash.uniqwith';
import lscache from 'lscache';
import { SortType } from 'modules/search/model/sorting';

import { STATUS_ERROR, STATUS_PENDING, STATUS_SUCCESS } from '../../constants';

export const constants = {
  ADD_RECENT_SEARCH: 'SEARCH:ADD_RECENT_SEARCH',
  FETCH_SEARCH: 'FETCH_SEARCH',
  FETCH_SEARCH_SUCCESS: 'FETCH_SEARCH_SUCCESS',
  FETCH_FAMILY_FRIENDLY_LIST: 'FETCH_FAMILY_FRIENDLY_LIST',
  FETCH_FAMILY_FRIENDLY_LIST_SUCCESS: 'FETCH_FAMILY_FRIENDLY_LIST_SUCCESS',
  FETCH_FAMILY_FRIENDLY_LIST_ERROR: 'FETCH_FAMILY_FRIENDLY_LIST_ERROR',
  SET_SORTING: 'SET_SORTING',
};

function getKey(filters) {
  // Remove page filter so multiple pages can reuse the key
  const cacheObject = {
    ...omit(filters, ['page']),
    activeFilters: filters.activeFilters
      ? filters.activeFilters.map((filter) => omit(filter, ['defaultValue']))
      : [],
  };

  return JSON.stringify(cacheObject);
}

const localStorageKey = 'recent_searches';
const maxRecentSearches = 3;

const activitiesKeys = [
  'sports-leisure',
  'cooking-classes',
  'workshops',
  'home-dinners',
  'tastings',
  'wildlife-nature',
];

export const SearchStore = Fluxxor.createStore({
  initialize() {
    this.searchResults = {};
    this.familyFriendlyLists = {};
    this.recentSearches = [];
    this.sorting =
      new URLSearchParams(window.location.search).get('sort') ||
      SortType.DEFAULT;

    try {
      this.recentSearches = lscache.get(localStorageKey) || [];
      this.recentSearches = this.recentSearches
        .map((recentSearch) => {
          if (!recentSearch.dateFrom) {
            return {
              ...recentSearch,
              isInThePast: false,
            };
          }
          // change dates when in the past, or set to null when invalid
          const dates = validateDateFromStorage(
            recentSearch.dateFrom,
            recentSearch.dateTo,
          );
          return {
            ...recentSearch,
            dateFrom: dates.dateFrom,
            dateTo: dates.dateTo,
            isInThePast: dates.isInThePast,
          };
        })
        .filter((recentSearch) => !recentSearch?.isInThePast);
    } catch (e) {
      this.recentSearches = [];
      lscache.remove(localStorageKey);
    }

    this.bindActions(
      constants.ADD_RECENT_SEARCH,
      this.onAddRecentSearch,
      constants.FETCH_SEARCH,
      this.onFetchSearchResults,
      constants.FETCH_SEARCH_SUCCESS,
      this.onFetchSearchResultsSuccess,
      constants.FETCH_FAMILY_FRIENDLY_LIST,
      this.onFetchFamilyFriendlyList,
      constants.FETCH_FAMILY_FRIENDLY_LIST_SUCCESS,
      this.onFetchFamilyFriendlyListSuccess,
      constants.FETCH_FAMILY_FRIENDLY_LIST_ERROR,
      this.onFetchFamilyFriendlyListError,
      constants.SET_SORTING,
      this.onSetSorting,
    );
  },

  onAddRecentSearch({ filters }) {
    const recentSearch = {
      country: filters.country,
      city: filters.city,
      adults: filters?.activeFilters?.find((f) => f.uid === 'adults')?.value,
      children: filters?.activeFilters?.find((f) => f.uid === 'children')
        ?.value,
      dateFrom: filters?.activeFilters?.find((f) => f.uid === 'date.from')
        ?.value,
      dateTo: filters?.activeFilters?.find((f) => f.uid === 'date.to')?.value,
      familyFriendly: filters.activeFilters.some(
        (f) => f.uid === 'family-friendly',
      ),
    };

    if (!recentSearch.country) {
      return;
    }

    this.recentSearches = uniqWith(
      [recentSearch, ...this.recentSearches],
      (x, y) => x.city === y.city && x.country === y.country,
    ).slice(0, maxRecentSearches);

    lscache.set(localStorageKey, this.recentSearches);
    this.emit('change');
  },

  onFetchSearchResults(filters) {
    const key = getKey(filters);
    const oldResults = this.searchResults[key];

    this.searchResults[key] = {
      pages: [
        ...(oldResults?.pages ?? []).filter(
          ({ page }) => page !== filters.page,
        ),
        {
          status: STATUS_PENDING,
          data: [],
          page: filters.page,
        },
      ].sort((a, b) => a.page - b.page),
      title: oldResults?.title ?? '',
      titleKeys: oldResults?.titleKeys ?? [],
      titleAboveSearchResults: oldResults?.titleAboveSearchResults ?? '',
      titleAboveSearchResultsKeys:
        oldResults?.titleAboveSearchResultsKeys ?? [],
      seeAllForCurrent: oldResults?.seeAllForCurrent ?? '',
      pagetitle: oldResults?.pagetitle ?? '',
      description: oldResults?.description ?? '',
      seapage: oldResults?.seapage ?? {},
      seoContentBlock: oldResults?.seoContentBlock ?? {},
      categoryBlockDescription: oldResults?.categoryBlockDescription ?? '',
      categoryBlockTitle: oldResults?.categoryBlockTitle ?? '',
      interlinks: oldResults?.interlinks ?? [],
      topSearchesTitleText: oldResults?.topSearchesTitleText ?? '',
      topSearchesTitleKeys: oldResults?.topSearchesTitleKeys ?? [],
      maxResults: oldResults?.maxResults,
      otherBySubcategory: oldResults?.otherBySubcategory,
      otherThingsToDo: oldResults?.otherThingsToDo,
      totalNumberOfResults: oldResults?.all?.hits?.total ?? 0,
    };
    this.emit('change');
  },

  onFetchSearchResultsSuccess({ filters, result }) {
    const key = getKey(filters);
    const oldResults = this.searchResults[key];

    this.searchResults[key] = {
      pages: [
        ...(oldResults?.pages ?? []).filter(
          ({ page }) => page !== filters.page,
        ),
        {
          status: STATUS_SUCCESS,
          data: result.all.hits.hits,
          page: filters.page,
          aggregations: result.all.aggregations,
        },
      ].sort((a, b) => a.page - b.page),
      title: result.title,
      titleKeys: result.titleKeys,
      titleAboveSearchResults: result.titleAboveSearchResults,
      titleAboveSearchResultsKeys: result.titleAboveSearchResultsKeys,
      seeAllForCurrent: result.seeAllForCurrent,
      pagetitle: result.pagetitle,
      seapage: result.seapage,
      seoContentBlock: result.seoContentBlock,
      categoryBlockDescription: result.categoryBlockDescription,
      categoryBlockTitle: result.categoryBlockTitle,
      interlinks: result.interlinks,
      topSearchesTitleText: result.topSearchesTitle,
      topSearchesTitleKeys: result.topSearchesTitleKeys,
      description: result.description,
      maxResults: result.all.hits.total,
      hostAggregations: result.hosts,
      otherBySubcategory: result?.otherBySubcategory?.hits?.hits,
      otherThingsToDo:
        result.otherThingsToDo
          .map((otherThingTodo) => ({
            title: otherThingTodo.title,
            data: otherThingTodo?.results?.hits?.hits,
          }))
          .filter((otherThingTodo) => !!otherThingTodo.title) || [],
      totalNumberOfResults: result?.all?.hits?.total ?? 0,
    };
    this.emit('change');
  },

  onFetchFamilyFriendlyList({ country, city, lang }) {
    const key = getKey({ country, city, lang });
    this.familyFriendlyLists[key] = {
      data: null,
      status: STATUS_PENDING,
    };
    this.emit('change');
  },

  onFetchFamilyFriendlyListSuccess({ country, city, lang, result }) {
    const key = getKey({ country, city, lang });
    this.familyFriendlyLists[key] = {
      data: result,
      status: STATUS_SUCCESS,
    };
    this.emit('change');
  },

  onFetchFamilyFriendlyListError({ country, city, lang }) {
    const key = getKey({ country, city, lang });
    this.familyFriendlyLists[key] = {
      data: null,
      status: STATUS_ERROR,
    };
    this.emit('change');
  },

  onSetSorting(sorting) {
    this.sorting = sorting;
    this.emit('change');
  },

  getRecentSearches() {
    return this.recentSearches;
  },

  getSearchResult(filters) {
    const key = getKey(filters);
    return this.searchResults[key];
  },

  getAggregations(filters) {
    const searchResults = this.getSearchResult(filters);
    if (
      searchResults &&
      searchResults.pages &&
      searchResults.pages.length &&
      searchResults.pages[0].aggregations
    ) {
      // add activities
      const aggregations = searchResults.pages[0].aggregations;
      if (
        aggregations.subcategories &&
        aggregations.subcategories.buckets &&
        !aggregations.subcategories.buckets.some((i) => i.key === 'activities')
      ) {
        const activitiesDocs = aggregations.subcategories.buckets
          .filter((s) => activitiesKeys.includes(s.key))
          .map((s) => s.doc_count)
          .reduce((sum, c) => sum + c, 0);
        aggregations.subcategories.buckets.push({
          key: 'activities',
          doc_count: activitiesDocs,
        });
      }
      return aggregations;
    }
    return null;
  },

  getFamilyFriendlyList({ country, city, lang }) {
    const key = getKey({ country, city, lang });
    return this.familyFriendlyLists[key];
  },

  getSorting() {
    return this.sorting;
  },
});
